/*
  Search drawer.

  Opens like the cart, from the right, and fetches results as the shopper
  types. Two requests go out together for every search:

    the suggest endpoint, for Shopify's suggested terms and collections,
    drawn by the predictive-search section

    the full search, for the products, drawn by the search-drawer-products
    section. The suggest endpoint returns at most ten products padded with
    loose matches, so a product named exactly what was typed could be
    missing; the full search finds every match, and that section puts the
    ones whose name matches first

  The products are put into the slot the first one leaves for them, and the
  two arrive on screen together as one block. The shape of every result is
  decided in Liquid rather than assembled here from JSON.

  Also here:

    chips, the popular and recent searches, search inside the drawer
    rather than leaving the page

    recent searches, the shopper's own last five, kept in her browser only
    and saved when she acts on a search: opens a result, presses enter or
    taps a chip. Not on every pause in typing, which would fill the list
    with half words

    when a search finds nothing, the chips and the featured row show again
    under the message, so the drawer is never a dead end

    the words typed are set in bold in each product name, so it is clear at
    a glance why a product is there

  On desktop the search box in the header is the field itself: typing
  there opens the same panel as a dropdown under the box (.is-dropdown),
  rather than the drawer from the right with a field of its own.

  Replaces Dawn's predictive-search.js, which is not loaded.
*/
if (!customElements.get('search-drawer')) {
  // Must match the closing transition in search-drawer.css.
  const CLOSE_MS = 350;

  /*
    Long enough that typing a word is one request rather than five, short
    enough that results feel like they are keeping up. Every keystroke
    cancels the previous timer, so only a pause actually sends anything.
  */
  const TYPING_MS = 200;

  // Below this there is nothing useful to suggest: a single letter matches
  // most of the catalogue and the request is wasted.
  const MIN_CHARS = 2;

  // Where the recent searches are kept, and how many.
  const RECENT_KEY = 'rm:recent-searches';
  const RECENT_MAX = 5;

  function escapeHtml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeRegex(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /*
    Sets the words typed in bold inside every product name under root, the
    name on the redesign's card (home-card). Case is ignored and words of
    one letter are left alone. The name is rebuilt from its own text,
    escaped first, so nothing typed can ever become markup.
  */
  function highlight(root, term) {
    const words = term
      .toLowerCase()
      .split(/\s+/)
      .filter((word) => word.length > 1)
      .map(escapeRegex);
    if (!words.length) return;

    const pattern = new RegExp(`(${words.join('|')})`, 'gi');

    root.querySelectorAll('.home-card__name').forEach((heading) => {
      const text = heading.textContent.trim();
      heading.innerHTML = escapeHtml(text).replace(pattern, '<strong class="search-hit">$1</strong>');
    });
  }

  class SearchDrawer extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('[data-search-form]');
      this.defaultView = this.querySelector('[data-search-default]');
      this.results = this.querySelector('[data-search-results]');
      this.recentBlock = this.querySelector('[data-search-recent]');
      this.recentList = this.querySelector('[data-search-recent-list]');

      this.closeTimer = null;
      this.typingTimer = null;

      // Bumped on every request. A response whose ticket is out of date is
      // thrown away, so a slow answer for "la" cannot overwrite a fast one
      // for "lace".
      this.ticket = 0;

      // Results already fetched this visit, keyed by term. Backspacing
      // through a word then typing it again costs nothing.
      this.cache = new Map();

      /*
        The full search's address, from the suggest endpoint's, so it keeps
        any language or market prefix the storefront uses, such as /en-us.
      */
      this.searchUrl = (window.routes.predictive_search_url || '/search/suggest').replace(/\/suggest\/?$/, '');

      this.addEventListener('click', this.onClick.bind(this));
      this.onKeydown = this.onKeydown.bind(this);

      if (this.input) {
        this.input.addEventListener('input', this.onInput.bind(this));
      }

      // Enter goes to the full results page, and counts as a search made.
      if (this.form) {
        this.form.addEventListener('submit', () => this.remember(this.input.value));
      }

      this.renderRecent();

      /*
        Both header search links open the drawer: the icon on desktop and
        the one in the mobile bar. They stay real links to /search, so they
        work with no script and for anyone opening them in a new tab.
      */
      document.querySelectorAll('[href$="/search"], .site-header__action--search, .site-header__action--search-mobile')
        .forEach((link) => {
          link.addEventListener('click', (event) => {
            // Let a modified click through, so open in new tab still works.
            if (event.metaKey || event.ctrlKey || event.shiftKey) return;

            event.preventDefault();
            this.open();
          });
        });

      this.setupHeaderBox();
    }

    /* The desktop header box --------------------------------------------- */

    /*
      Focus in the box opens the panel under it, and what is typed there is
      searched as if typed in the panel's own field, which stays hidden.
      Enter submits the box to the full results page. A click anywhere
      outside the box and the panel closes it, as does Escape. Without this
      script the box is a plain form to /search.
    */
    setupHeaderBox() {
      this.box = document.querySelector('.site-header__search');
      this.boxField = this.box && this.box.querySelector('input[type="search"]');
      if (!this.boxField) return;

      this.onOutside = this.onOutside.bind(this);

      this.boxField.addEventListener('focus', () => {
        if (!this.classList.contains('is-open')) this.open({ anchor: this.box });
      });

      this.boxField.addEventListener('input', () => {
        if (!this.input) return;
        this.input.value = this.boxField.value;
        this.onInput();
      });

      this.box.addEventListener('submit', () => this.remember(this.boxField.value));
    }

    // Under the box, lined up with its left edge, and the page darkened
    // from the bottom of the header down.
    placeDropdown() {
      const box = this.box.getBoundingClientRect();
      const bar = document.querySelector('[data-header-bar]');
      const barBottom = bar ? bar.getBoundingClientRect().bottom : box.bottom;
      this.style.setProperty('--sd-top', `${Math.round(box.bottom + 8)}px`);
      this.style.setProperty('--sd-left', `${Math.round(box.left)}px`);
      this.style.setProperty('--sd-scrim-top', `${Math.round(Math.max(barBottom, 0))}px`);
    }

    onOutside(event) {
      if (this.box.contains(event.target)) return;
      const panel = this.querySelector('.search-drawer__panel');
      if (panel && panel.contains(event.target)) return;
      this.close();
    }

    /* Featured row ------------------------------------------------------ */

    /*
      The featured products ship inside a template element, which the
      browser parses but does not lay out or style. Moving them in is a
      clone and a swap, so the row is there the moment the panel arrives.
      Done once per page.
    */
    hydrateFeatured() {
      const template = this.querySelector('[data-search-featured]');
      if (!template) return;

      template.replaceWith(template.content.cloneNode(true));

      if (window.attachDragScroll) {
        this.querySelectorAll('[data-drag-scroll]').forEach(window.attachDragScroll);
      }
    }

    /* Open and close ---------------------------------------------------- */

    open(options = {}) {
      clearTimeout(this.closeTimer);

      const dropdown = Boolean(options.anchor);
      this.classList.toggle('is-dropdown', dropdown);
      if (dropdown) {
        this.placeDropdown();
        document.addEventListener('pointerdown', this.onOutside, true);
      }

      // Before the panel is shown, so the row is in place for the first
      // frame rather than appearing a moment into the animation.
      this.hydrateFeatured();

      this.hidden = false;

      // A frame between being rendered and being animated. Without it the
      // browser has nothing to transition from and the panel just appears.
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('search-open');

        // Focused after the panel has started moving. Focusing a field that
        // is still offscreen makes some browsers scroll the page to it.
        // As a dropdown, the focus stays in the header box.
        if (this.input && !dropdown) this.input.focus();
      });

      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (this.hidden) return;

      this.classList.remove('is-open');
      document.body.classList.remove('search-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
      if (this.boxField && document.activeElement === this.boxField) this.boxField.blur();

      this.closeTimer = setTimeout(() => {
        this.hidden = true;
      }, CLOSE_MS);
    }

    onKeydown(event) {
      // Quick add opens over the drawer; Escape closes that first.
      if (event.key === 'Escape' && !document.body.classList.contains('qa-open')) this.close();
    }

    onClick(event) {
      if (event.target.closest('[data-search-close]')) {
        this.close();
        return;
      }

      const chip = event.target.closest('[data-search-term]');
      if (chip) {
        event.preventDefault();
        this.runTerm(chip.dataset.searchTerm);
        return;
      }

      if (event.target.closest('[data-search-recent-clear]')) {
        this.clearRecent();
        return;
      }

      const tab = event.target.closest('[data-ps-tab]');
      if (tab) {
        this.showTab(tab.dataset.psTab);
        return;
      }

      // Opening anything from the results counts as a search made.
      if (event.target.closest('a') && this.results && this.results.contains(event.target)) {
        this.remember(this.input.value);
      }
    }

    /* Tabs -------------------------------------------------------------- */

    showTab(name) {
      this.querySelectorAll('[data-ps-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.psTab === name));
      });

      this.querySelectorAll('[data-ps-panel]').forEach((panel) => {
        panel.toggleAttribute('hidden', panel.dataset.psPanel !== name);
      });
    }

    /* Recent searches --------------------------------------------------- */

    /*
      Kept in the shopper's own browser and nowhere else. Every read and
      write is guarded: private modes and full storage throw, and recent
      searches are a convenience, never a reason for search to break.
    */
    readRecent() {
      try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
      } catch (error) {
        return [];
      }
    }

    writeRecent(list) {
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch (error) {
        // Storage unavailable. The list simply is not kept.
      }
    }

    // Most recent first, no duplicates whatever their case, five at most.
    remember(value) {
      const term = (value || '').trim();
      if (term.length < MIN_CHARS) return;

      const list = this.readRecent().filter((item) => item.toLowerCase() !== term.toLowerCase());
      list.unshift(term);
      this.writeRecent(list.slice(0, RECENT_MAX));
      this.renderRecent();
    }

    clearRecent() {
      this.writeRecent([]);
      this.renderRecent();
    }

    renderRecent() {
      if (!this.recentBlock || !this.recentList) return;

      const list = this.readRecent();
      this.recentList.innerHTML = '';

      list.forEach((term) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const params = new URLSearchParams({ q: term, type: 'product', 'options[prefix]': 'last' });

        link.className = 'search-drawer__chip';
        link.href = `${this.searchUrl}?${params}`;
        link.dataset.searchTerm = term;
        link.textContent = term;

        item.appendChild(link);
        this.recentList.appendChild(item);
      });

      this.recentBlock.hidden = list.length === 0;
    }

    /* Fetching ---------------------------------------------------------- */

    // A chip: the term goes into the field and is searched straight away.
    runTerm(term) {
      if (!this.input || !term) return;

      this.input.value = term;
      if (this.boxField && this.classList.contains('is-dropdown')) this.boxField.value = term;
      clearTimeout(this.typingTimer);
      this.remember(term);
      this.search(term);
    }

    onInput() {
      const term = this.input.value.trim();

      clearTimeout(this.typingTimer);

      // Cleared field goes back to the default view rather than sitting
      // empty, so the drawer always has something in it.
      if (term.length < MIN_CHARS) {
        this.showDefault();
        return;
      }

      this.typingTimer = setTimeout(() => this.search(term), TYPING_MS);
    }

    showDefault() {
      this.renderRecent();
      if (this.defaultView) this.defaultView.hidden = false;
      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
    }

    async search(term) {
      const ticket = (this.ticket += 1);

      try {
        const markup = await this.fetchResults(term);

        // A newer request has been sent since this one left, so its answer
        // is the one that matters.
        if (ticket !== this.ticket) return;

        const found = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-ps]');
        if (!found) return;

        highlight(found, term);

        /*
          With products the results replace the default view. With none,
          the default view stays under the message: the chips and the
          featured row, so there is always something to tap.
        */
        const nothing = !found.querySelector('.ps__grid');
        if (this.defaultView) this.defaultView.hidden = !nothing;
        if (nothing) this.renderRecent();

        this.results.innerHTML = '';
        this.results.appendChild(found);
        this.results.hidden = false;

        // The suggested terms scroll sideways, so the shared drag handler
        // has to be told about markup that did not exist a moment ago.
        if (window.attachDragScroll) {
          this.results.querySelectorAll('[data-drag-scroll]').forEach(window.attachDragScroll);
        }
      } catch (error) {
        // A failed lookup leaves whatever was on screen. The field still
        // submits to the full search page, which is the reliable path.
        if (ticket === this.ticket) this.showDefault();
      }
    }

    /*
      Both requests for a term, combined into one block: the suggested terms
      and collections, with the products put into the slot left for them.
      Products only, pages and articles left out.
    */
    fetchResults(term) {
      if (this.cache.has(term)) return this.cache.get(term);

      const suggest = new URLSearchParams({
        q: term,
        'resources[type]': 'collection,query',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });

      const full = new URLSearchParams({
        q: term,
        type: 'product',
        'options[prefix]': 'last',
        section_id: 'search-drawer-products',
      });

      const read = (url) =>
        fetch(url).then((response) => {
          if (!response.ok) throw new Error('Request failed');
          return response.text();
        });

      const request = Promise.all([
        read(`${window.routes.predictive_search_url}?${suggest}`),
        read(`${this.searchUrl}?${full}`),
      ]).then(([suggestMarkup, productMarkup]) => {
        const parser = new DOMParser();
        const block = parser.parseFromString(suggestMarkup, 'text/html').querySelector('[data-ps]');
        const products = parser.parseFromString(productMarkup, 'text/html').querySelector('[data-sdp]');
        if (!block) throw new Error('No results block');

        const slot = block.querySelector('[data-ps-products]');
        if (slot && products) slot.appendChild(products);

        return block.outerHTML;
      });

      this.cache.set(term, request);

      // A failed lookup should not be remembered, or that term stays broken
      // for the rest of the visit.
      request.catch(() => this.cache.delete(term));

      return request;
    }
  }

  customElements.define('search-drawer', SearchDrawer);
}
